import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchPagesIndex, type PagesIndex } from '../lib/pages';
import { useFileSystemDuplexer } from '../lib/use-file-system-duplexer';

// Whether the user has a home page — a grid page that `pages.json`'s `home` pointer designates — and,
// if so, the pages index that drives it (the home slug + the page list for the switcher). Probed once
// and shared by both the `/` landing (which renders the home grid instead of the chat) and the
// floating chat (which appears on `/` only when a home page is showing). No home pointer → absent →
// the chat stays the landing.
export type HomepageState =
  | { status: 'loading' }
  | { status: 'present'; index: PagesIndex }
  | { status: 'absent' };

const HomepageContext = createContext<{ state: HomepageState; refresh: () => void }>({
  state: { status: 'loading' },
  refresh: () => {},
});

export function HomepageProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<HomepageState>({ status: 'loading' });

  const load = useCallback(() => {
    let cancelled = false;
    fetchPagesIndex()
      .then((index) => {
        if (!cancelled) setState(index.home ? { status: 'present', index } : { status: 'absent' });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'absent' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setState({ status: 'loading' });
    return load();
  }, [load]);

  // Live update: when Orion creates the first page, sets a different home, or edits the grid, the VFS
  // changes and the file-system-changed signal fires — re-probe so `/` reflects it without a reload.
  useFileSystemDuplexer(() => {
    load();
  });

  return <HomepageContext.Provider value={{ state, refresh: load }}>{children}</HomepageContext.Provider>;
}

export function useHomepage() {
  return useContext(HomepageContext);
}
